import { buildApiUrl } from "./runtime-config";

export interface AdminLoginResponse {
  token: string;
  email: string;
  expiresAt: string;
}

export class AdminApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = "AdminApiError";
  }
}

export interface AdminUser {
  userId: string;
  email?: string | null;
  nickname: string;
  avatarUrl: string;
  region: string;
  generationCredits: number;
  petCount: number;
  petLimit: number;
  isDisabled: boolean;
  lastLoginAt?: string | null;
}

export interface AdminUserOperationResponse {
  message: string;
  user: AdminUser;
}

export interface AdminPaymentOrder {
  orderId: string;
  userId: string;
  userNickname: string;
  region: string;
  channel: string;
  productId: string;
  title: string;
  credits: number;
  petSlots: number;
  amountCny: number;
  currency: string;
  status: string;
  providerTradeNo?: string | null;
  refundTradeNo?: string | null;
  refundReason?: string | null;
  createdAt: string;
  updatedAt: string;
  paidAt?: string | null;
  refundedAt?: string | null;
}

export interface AdminPaymentProduct {
  productId: string;
  productType: "credits" | "pet_slot";
  title: string;
  description: string;
  region: string;
  credits: number;
  petSlots: number;
  amountCny: number;
  currency: string;
  status: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPaymentProductOperationResponse {
  message: string;
  product: AdminPaymentProduct;
}

export interface AdminStripeCatalogSyncItem {
  productId: string;
  title: string;
  stripeProductId: string;
  stripePriceId: string;
  status: string;
  message: string;
}

export interface AdminStripeCatalogSyncResponse {
  message: string;
  totalProducts: number;
  syncedProducts: number;
  items: AdminStripeCatalogSyncItem[];
}

export interface AdminSecretField {
  hasValue: boolean;
  preview: string;
}

export interface AdminJimengSettings {
  accessKeyId: string;
  secretAccessKey: AdminSecretField;
}

export interface AdminWechatMerchantSettings {
  paused: boolean;
  merchantId: string;
  appId: string;
  apiV3Key: AdminSecretField;
  certificateSerialNo: string;
  privateKey: AdminSecretField;
  publicKey: AdminSecretField;
  publicKeyId: string;
}

export interface AdminGoogleSettings {
  clientId: string;
  clientSecret: AdminSecretField;
  enabled: boolean;
}

export interface AdminWechatAuthSettings {
  appId: string;
  appSecret: AdminSecretField;
  enabled: boolean;
}

export interface AdminStripeSettings {
  enabled: boolean;
  publishableKey: string;
  secretKey: AdminSecretField;
  webhookSecret: AdminSecretField;
  checkoutUrlTemplate: string;
}

export interface AdminSettingsOperationResponse {
  message: string;
}

export interface AdminRegistrationLimitSettings {
  chinaLimit: number;
  chinaCurrent: number;
  globalLimit: number;
  globalCurrent: number;
}

export async function loginAdmin(email: string, password: string): Promise<AdminLoginResponse> {
  return requestAdminJson<AdminLoginResponse>("/api/admin/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ email, password }),
    skipAuth: true
  });
}

export async function listAdminUsers(): Promise<AdminUser[]> {
  return requestAdminJson<AdminUser[]>("/api/admin/users");
}

export async function resetAdminUserPassword(userId: string, newPassword: string): Promise<AdminUserOperationResponse> {
  return requestAdminJson<AdminUserOperationResponse>(`/api/admin/users/${encodeURIComponent(userId)}/password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ newPassword })
  });
}

export async function setAdminUserStatus(userId: string, isDisabled: boolean): Promise<AdminUserOperationResponse> {
  return requestAdminJson<AdminUserOperationResponse>(`/api/admin/users/${encodeURIComponent(userId)}/status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ isDisabled })
  });
}

export async function adjustAdminUserCredits(userId: string, credits: number, reason: string): Promise<AdminUserOperationResponse> {
  return requestAdminJson<AdminUserOperationResponse>(`/api/admin/users/${encodeURIComponent(userId)}/credits`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ credits, reason })
  });
}

export async function deleteAdminUser(userId: string): Promise<void> {
  await requestAdminJson<void>(`/api/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" });
}

export async function listAdminOrders(): Promise<AdminPaymentOrder[]> {
  return requestAdminJson<AdminPaymentOrder[]>("/api/admin/orders");
}

export async function refundAdminOrder(orderId: string, reason: string): Promise<{ message: string; order: AdminPaymentOrder }> {
  return requestAdminJson<{ message: string; order: AdminPaymentOrder }>(
    `/api/admin/orders/${encodeURIComponent(orderId)}/refund`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ reason })
    }
  );
}

export async function deleteAdminOrder(orderId: string): Promise<void> {
  await requestAdminJson<void>(`/api/admin/orders/${encodeURIComponent(orderId)}`, { method: "DELETE" });
}

export async function listAdminPaymentProducts(): Promise<AdminPaymentProduct[]> {
  return requestAdminJson<AdminPaymentProduct[]>("/api/admin/payment-products");
}

export async function saveAdminPaymentProduct(product: Record<string, unknown>): Promise<AdminPaymentProductOperationResponse> {
  return requestAdminJson<AdminPaymentProductOperationResponse>("/api/admin/payment-products", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(product)
  });
}

export async function deleteAdminPaymentProduct(productId: string): Promise<void> {
  await requestAdminJson<void>(`/api/admin/payment-products/${encodeURIComponent(productId)}`, { method: "DELETE" });
}

export async function syncAdminPaymentProductsToStripe(): Promise<AdminStripeCatalogSyncResponse> {
  return requestAdminJson<AdminStripeCatalogSyncResponse>("/api/admin/payment-products/sync-stripe", { method: "POST" });
}

export interface AdminPetAction {
  key: string;
  name: string;
  nameZh: string;
  nameEn: string;
  promptSnippet: string;
  isDefault: boolean;
  creditCost: number;
  sortOrder: number;
  enabled: boolean;
  mirrored: boolean;
  fps: number;
  loop: boolean;
  message: string;
  messageZh: string;
  messageEn: string;
}

export async function listAdminPetActions(): Promise<AdminPetAction[]> {
  return requestAdminJson<AdminPetAction[]>("/api/admin/pet-actions");
}

export async function saveAdminPetAction(action: AdminPetAction): Promise<{ message: string; action: AdminPetAction }> {
  return requestAdminJson("/api/admin/pet-actions", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(action)
  });
}

export async function deleteAdminPetAction(key: string): Promise<void> {
  await requestAdminJson<void>(`/api/admin/pet-actions/${encodeURIComponent(key)}`, { method: "DELETE" });
}

export async function getAdminJimengSettings(): Promise<AdminJimengSettings> {
  return requestAdminJson<AdminJimengSettings>("/api/admin/settings/jimeng");
}

export async function saveAdminJimengSettings(settings: Record<string, unknown>): Promise<AdminSettingsOperationResponse> {
  return requestAdminJson<AdminSettingsOperationResponse>("/api/admin/settings/jimeng", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(settings)
  });
}

export async function getAdminGoogleSettings(): Promise<AdminGoogleSettings> {
  return requestAdminJson<AdminGoogleSettings>("/api/admin/settings/google");
}

export async function saveAdminGoogleSettings(settings: Record<string, unknown>): Promise<AdminSettingsOperationResponse> {
  return requestAdminJson<AdminSettingsOperationResponse>("/api/admin/settings/google", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(settings)
  });
}

export async function getAdminWechatAuthSettings(): Promise<AdminWechatAuthSettings> {
  return requestAdminJson<AdminWechatAuthSettings>("/api/admin/settings/wechat-auth");
}

export async function saveAdminWechatAuthSettings(settings: Record<string, unknown>): Promise<AdminSettingsOperationResponse> {
  return requestAdminJson<AdminSettingsOperationResponse>("/api/admin/settings/wechat-auth", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(settings)
  });
}

export async function getAdminStripeSettings(): Promise<AdminStripeSettings> {
  return requestAdminJson<AdminStripeSettings>("/api/admin/settings/stripe");
}

export async function saveAdminStripeSettings(settings: Record<string, unknown>): Promise<AdminSettingsOperationResponse> {
  return requestAdminJson<AdminSettingsOperationResponse>("/api/admin/settings/stripe", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(settings)
  });
}

export async function getAdminRegistrationLimitSettings(): Promise<AdminRegistrationLimitSettings> {
  return requestAdminJson<AdminRegistrationLimitSettings>("/api/admin/settings/registration-limits");
}

export async function saveAdminRegistrationLimitSettings(settings: {
  chinaLimit: number;
  globalLimit: number;
}): Promise<AdminSettingsOperationResponse> {
  return requestAdminJson<AdminSettingsOperationResponse>("/api/admin/settings/registration-limits", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(settings)
  });
}

export async function getAdminWechatMerchantSettings(): Promise<AdminWechatMerchantSettings> {
  return requestAdminJson<AdminWechatMerchantSettings>("/api/admin/settings/wechat-merchant");
}

export interface SaveWechatMerchantInput {
  paused: boolean;
  merchantId: string;
  appId: string;
  apiV3Key?: string;
  certificateSerialNo: string;
  publicKeyId: string;
  privateKeyFile?: File | null;
  publicKeyFile?: File | null;
}

export async function saveAdminWechatMerchantSettings(input: SaveWechatMerchantInput): Promise<AdminSettingsOperationResponse> {
  // PEM 走文件上传，用 multipart/form-data；不手动设 Content-Type，让浏览器带上 boundary。
  const formData = new FormData();
  formData.set("Paused", String(input.paused));
  formData.set("MerchantId", input.merchantId);
  formData.set("AppId", input.appId);
  formData.set("CertificateSerialNo", input.certificateSerialNo);
  formData.set("PublicKeyId", input.publicKeyId);
  if (input.apiV3Key) {
    formData.set("ApiV3Key", input.apiV3Key);
  }
  if (input.privateKeyFile) {
    formData.set("PrivateKeyFile", input.privateKeyFile);
  }
  if (input.publicKeyFile) {
    formData.set("PublicKeyFile", input.publicKeyFile);
  }
  return requestAdminJson<AdminSettingsOperationResponse>("/api/admin/settings/wechat-merchant", {
    method: "PUT",
    body: formData
  });
}

interface AdminRequestInit extends RequestInit {
  skipAuth?: boolean;
}

async function requestAdminJson<T>(path: string, init?: AdminRequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!init?.skipAuth) {
    const token = localStorage.getItem("pet.admin.token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  headers.set("X-App-Locale", "zh-CN");
  headers.set("X-App-Region", "CN");

  const response = await safeAdminFetch(path, {
    ...init,
    headers
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401 && !init?.skipAuth) {
      handleUnauthorizedAdminResponse();
    }
    throw new AdminApiError(json?.message || `管理后台接口请求失败：HTTP ${response.status}`, response.status, json?.error);
  }
  return json as T;
}

async function safeAdminFetch(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(buildApiUrl(path), init);
  } catch {
    throw new AdminApiError("暂时连接不上管理服务，请检查网络或稍后再试。", 0, "network_error");
  }
}

function handleUnauthorizedAdminResponse() {
  clearAdminAuthStorage(localStorage);
  if (typeof sessionStorage !== "undefined") {
    clearAdminAuthStorage(sessionStorage);
  }

  const currentLocation = globalThis.location;
  if (currentLocation?.pathname !== "/admin/login") {
    currentLocation?.assign?.("/admin/login");
  }
}

function clearAdminAuthStorage(storage: Storage) {
  const keysToRemove: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) {
      continue;
    }

    const normalizedKey = key.toLowerCase();
    if (normalizedKey === "pet.admin.token" || normalizedKey.startsWith("pet.admin.") || normalizedKey.includes("jwt")) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}
